import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { useChartPeriod } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';
import {
  CurrencyValue,
  Flex,
  LineChart,
  formatNumber,
  DisplaySwitch,
  BarChart,
  formatDatetime
} from '@blockanalitica/ui';
import { useMemo, useState, useCallback } from 'react';
import { useTheme } from 'styled-components';

export default function HistoricTabs() {
  const theme = useTheme();
  const [activeDisplayOption, setActiveDisplayOption] =
    useState('sky_buyback_24h');
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();

  const displayOptions = useMemo(
    () => [
      { key: 'sky_buyback_24h', value: 'Daily buyback' },

      { key: 'sky_cumulative_buyback', value: 'SKY Cumulative Buyback' }
    ],
    []
  );

  const [data, error] = useApi({
    url: '/buyback/historic/',
    queryParams: { days_ago: chartDaysAgo }
  });

  if (error) throw error;

  const lineData = data?.line ?? [];
  const barData = data?.bar ?? [];

  const timeFilter = (
    <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
  );

  const datasets = useMemo(
    () => [
      {
        xField: 'date',
        yField: 'sky_buyback_24h',
        label: 'Sky Buybacks',
        backgroundColor: theme.colors.chartUp
      }
    ],
    [theme.colors]
  );

  const yScaleValueFormatter = (value) => formatNumber(value);

  const valueFormatter = (value) => {
    switch (activeDisplayOption) {
      case 'net_profit':
        return (
          <CurrencyValue
            value={value}
            options={{
              currency: 'USD',
              iconSize: '3'
            }}
          />
        );
      default:
        return (
          <CurrencyValue
            value={value}
            options={{
              currency: tokensMapping('SKY'),
              iconSize: '3'
            }}
          />
        );
    }
  };

  const subValueFormatter = useCallback(
    (value) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  if (lineData.length > 0 || barData.length > 0) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />

        {activeDisplayOption === 'sky_buyback_24h' ? (
          <BarChart
            key={activeDisplayOption}
            data={barData}
            datasets={datasets}
            valueFormatter={valueFormatter}
            yScaleValueFormatter={yScaleValueFormatter}
            timeFilter={timeFilter}
            subValueFormatter={subValueFormatter}
          />
        ) : (
          <LineChart
            key={activeDisplayOption}
            data={lineData}
            datasets={[{ xField: 'date', yField: activeDisplayOption }]}
            valueFormatter={valueFormatter}
            yScaleValueFormatter={yScaleValueFormatter}
            timeFilter={timeFilter}
            config={config}
          />
        )}
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
